import { includes, chain } from 'lodash';

import {
  PartnerCompanyTypes,
  PartnerCompanyStatusTypes,
  AppInternalType,
} from '~globals/types/enums';
import {
  PARTNER_COMPANY_TYPES,
  PARTNER_COMPANY_STATUS_TYPES,
} from '~constants/partnerCompany';
import { Nullable } from '~globals/types';
import {
  PartnerCompany,
  PartnerCompanyImporter,
} from '~services/partnerCompany/types';
import { AuthExtendedCompany } from '~services/auth/types';
import { pluralize } from '~utils/commons';
import { getStringifyUrlQueryString } from '~utils/router';

export const getPartnerCompanyTypeDescription = (
  typeId: PartnerCompanyTypes,
): string =>
  chain(PARTNER_COMPANY_TYPES)
    .find({ value: typeId })
    .get('label', '')
    .value() as string;

export const validatePartnerCompanyType = (
  typeId: PartnerCompanyTypes,
  validStates: PartnerCompanyTypes[],
): boolean => includes(validStates, typeId);

export const isCarrierPartnerCompany = (typeId: PartnerCompanyTypes): boolean =>
  validatePartnerCompanyType(typeId, [PartnerCompanyTypes.Carrier]);

export const isGiverPartnerCompany = (typeId: PartnerCompanyTypes): boolean =>
  validatePartnerCompanyType(typeId, [PartnerCompanyTypes.Giver]);

export const getPartnerCompanyStatusTypeDescription = (
  statusTypeId: PartnerCompanyStatusTypes,
): string =>
  chain(PARTNER_COMPANY_STATUS_TYPES)
    .find({ value: statusTypeId })
    .get('label', '')
    .value() as string;

export const validatePartnerCompanyStatusType = (
  statusTypeId: PartnerCompanyStatusTypes,
  validStates: PartnerCompanyStatusTypes[],
): boolean => includes(validStates, statusTypeId);

export const isActivedPartnerCompany = (
  lastLoginDateTime: Nullable<string>,
): boolean => lastLoginDateTime !== null;

export const isPendingPartnerCompany = (
  lastLoginDateTime: Nullable<string>,
): boolean => lastLoginDateTime === null;

export const getPartnerCompanyCurrentCompanyInfo = (
  currentPartnerCompany: PartnerCompany,
): Nullable<AuthExtendedCompany> => {
  let currentCompany: Nullable<AuthExtendedCompany> = null;

  if (isCarrierPartnerCompany(currentPartnerCompany.partnerCompanyTypeId)) {
    currentCompany = currentPartnerCompany.carrierCompany;
  }

  if (isGiverPartnerCompany(currentPartnerCompany.partnerCompanyTypeId)) {
    currentCompany = currentPartnerCompany.giverCompany;
  }

  return currentCompany;
};

export const getSelectedPartnerCompanyInfo = (
  currentSelectdPartnerCompany: Nullable<PartnerCompany>,
): string => {
  let info = '';

  if (currentSelectdPartnerCompany) {
    const currentCompany = getPartnerCompanyCurrentCompanyInfo(
      currentSelectdPartnerCompany,
    );

    const currentParterCompanyTypeDescription =
      getPartnerCompanyTypeDescription(
        currentSelectdPartnerCompany.partnerCompanyTypeId,
      );

    info = `${currentParterCompanyTypeDescription} / ${
      currentCompany?.name ?? currentSelectdPartnerCompany.reference
    }`;
  }

  return info;
};

export const isCurrentLoggedCompany = (
  currentCompanyId: Nullable<string>,
  loggedCompanyId: Nullable<string>,
): boolean => currentCompanyId === loggedCompanyId;

export const getImporterPartnerCompaniesFromCSVAlertDescription = ({
  errorCount,
  successCount,
}: PartnerCompanyImporter): string | string[] => {
  const totalRows = errorCount + successCount;

  if (errorCount >= 1) {
    const sendQuantityMessage = `Se ${pluralize({
      singular: 'envió',
      plural: 'enviaron',
      count: totalRows,
    })} ${pluralize({
      singular: 'dador de carga',
      plural: 'dadores de carga',
      count: totalRows,
      includeCount: true,
    })}`;

    const importedWithSuccessRowsMessage = ` ${successCount} se ${pluralize({
      singular: 'importó',
      plural: 'importaron',
      count: successCount,
    })} correctamente`;

    const importedWithErrorRowsMessage = `${errorCount} se ${pluralize({
      singular: 'importó',
      plural: 'importaron',
      count: errorCount,
    })} con errores`;

    return [
      sendQuantityMessage,
      importedWithSuccessRowsMessage,
      importedWithErrorRowsMessage,
    ];
  }

  return `Se ${pluralize({
    singular: 'importó',
    plural: 'importaron',
    count: totalRows,
  })} ${pluralize({
    singular: 'dador de carga',
    plural: 'dadores de carga',
    count: totalRows,
    includeCount: true,
  })} correctamente`;
};

export const getInvitationMessage = (
  email: string,
  password: string,
): string => {
  const linkApp = getStringifyUrlQueryString({
    url: 'https://app.routix.io/public/device_redirect',
    query: {
      app: AppInternalType.Tiendas,
    },
  });

  const lines = [
    'Te damos acceso nuestra plataforma de seguimiento y gestión de envíos.',
    '',
    // eslint-disable-next-line max-len
    `Desde un solo lugar *vas a poder administrar todas tus órdenes, imprimir etiquetas y realizar el seguimiento en tiempo real de cada entrega, sin importar el canal de venta.*`,
    '',
    `*Usuario:* ${email}`,
    `*Contraseña:* ${password}`,
    '',
    '📲Descargá la aplicación👇:',
    linkApp,
  ];

  const message = lines.join('\n');

  return message;
};
